"use client";

import { useAuth } from "@clerk/nextjs";
import { useEffect, useRef } from "react";
import { trackLead } from "@/lib/utils/meta-pixel";

/**
 * Clerk 사용자를 Supabase DB에 자동으로 동기화하는 훅
 *
 * 사용자가 로그인한 상태에서 이 훅을 사용하면
 * 자동으로 /api/user/sync-user를 호출하여 Supabase users 테이블에 사용자 정보를 저장합니다.
 *
 * @example
 * ```tsx
 * 'use client';
 *
 * import { useSyncUser } from '@/hooks/shared/use-sync-user';
 *
 * export default function Layout({ children }) {
 *   useSyncUser();
 *   return <>{children}</>;
 * }
 * ```
 */
export function useSyncUser() {
  const { isLoaded, userId } = useAuth();
  const syncedRef = useRef(false);

  useEffect(() => {
    // 이미 동기화했거나, 로딩 중이거나, 로그인하지 않은 경우 무시
    if (syncedRef.current || !isLoaded || !userId) {
      return;
    }

    // 동기화 실행
    const syncUser = async () => {
      try {
        console.log("[useSyncUser] 사용자 동기화 시작...", { userId });
        
        const response = await fetch("/api/user/sync-user", {
          method: "POST",
        });

        if (!response.ok) {
          const errorText = await response.text();
          let errorData;
          try {
            errorData = JSON.parse(errorText);
          } catch {
            errorData = { error: errorText };
          }
          
          console.error("[useSyncUser] 사용자 동기화 실패:", {
            status: response.status,
            statusText: response.statusText,
            error: errorData,
          });
          return;
        }

        const result = await response.json();
        console.log("[useSyncUser] 사용자 동기화 성공:", result);
        
        // 새 사용자(회원가입 완료)인 경우 Meta Pixel Lead 이벤트 추적
        if (result.isNewUser) {
          console.log("[useSyncUser] 새 사용자 감지 - Meta Pixel Lead 이벤트 전송");
          trackLead({
            content_name: "Sign Up Completed",
            content_category: "User Registration",
            value: 0,
            currency: "KRW",
          });
        }
        
        syncedRef.current = true;
      } catch (error) {
        console.error("[useSyncUser] 사용자 동기화 중 예외 발생:", {
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
        });
      }
    };

    syncUser();
  }, [isLoaded, userId]);
}
